import { createSlice } from '@reduxjs/toolkit';
import type { MenuVisibility } from 'menu/types';
import {
  getCurrentUserVisibilityAsync,
  getResolvedUiVisibilityAsync,
  getOwnUiVisibilityAsync,
  putUiVisibilityAsync,
  deleteUiVisibilityAsync,
} from './asyncActions';

type Tree = Record<string, MenuVisibility>;
type Status = 'idle' | 'loading' | 'failed';

export interface UiVisibilityState {
  current: { tree: Tree | null; status: Status };
  resolvedByGroup: Record<string, { tree: Tree; status: Status }>;
  ownByGroup: Record<string, { tree: Tree; status: Status }>;
  saveStatus: Status;
}

const initialState: UiVisibilityState = {
  current: { tree: null, status: 'idle' },
  resolvedByGroup: {},
  ownByGroup: {},
  saveStatus: 'idle',
};

const uiVisibilitySlice = createSlice({
  name: 'uiVisibility',
  initialState,
  reducers: {
    resetCurrentVisibility: (state) => {
      state.current = { tree: null, status: 'idle' };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCurrentUserVisibilityAsync.pending, (state) => {
        state.current.status = 'loading';
      })
      .addCase(getCurrentUserVisibilityAsync.fulfilled, (state, action) => {
        state.current.status = 'idle';
        state.current.tree = action.payload || {};
      })
      .addCase(getCurrentUserVisibilityAsync.rejected, (state) => {
        state.current.status = 'failed';
      })

      .addCase(getResolvedUiVisibilityAsync.pending, (state, action) => {
        const id = action.meta.arg;
        state.resolvedByGroup[id] = { tree: state.resolvedByGroup[id]?.tree || {}, status: 'loading' };
      })
      .addCase(getResolvedUiVisibilityAsync.fulfilled, (state, action) => {
        const { groupId, tree } = action.payload;
        state.resolvedByGroup[groupId] = { tree, status: 'idle' };
      })
      .addCase(getResolvedUiVisibilityAsync.rejected, (state, action) => {
        const id = action.meta.arg;
        state.resolvedByGroup[id] = { tree: state.resolvedByGroup[id]?.tree || {}, status: 'failed' };
      })

      .addCase(getOwnUiVisibilityAsync.pending, (state, action) => {
        const id = action.meta.arg;
        state.ownByGroup[id] = { tree: state.ownByGroup[id]?.tree || {}, status: 'loading' };
      })
      .addCase(getOwnUiVisibilityAsync.fulfilled, (state, action) => {
        const { groupId, tree } = action.payload;
        state.ownByGroup[groupId] = { tree, status: 'idle' };
      })
      .addCase(getOwnUiVisibilityAsync.rejected, (state, action) => {
        const id = action.meta.arg;
        state.ownByGroup[id] = { tree: state.ownByGroup[id]?.tree || {}, status: 'failed' };
      })

      .addCase(putUiVisibilityAsync.pending, (state) => {
        state.saveStatus = 'loading';
      })
      .addCase(putUiVisibilityAsync.fulfilled, (state, action) => {
        state.saveStatus = 'idle';
        const { groupId, tree } = action.payload;
        state.ownByGroup[groupId] = { tree, status: 'idle' };
      })
      .addCase(putUiVisibilityAsync.rejected, (state) => {
        state.saveStatus = 'failed';
      })

      .addCase(deleteUiVisibilityAsync.fulfilled, (state, action) => {
        const { groupId } = action.payload;
        state.ownByGroup[groupId] = { tree: {}, status: 'idle' };
      });
  },
});

export const { resetCurrentVisibility } = uiVisibilitySlice.actions;
export default uiVisibilitySlice.reducer;
